import { MeditatorStatus, TableTitles } from "@/constants";
import { useGetMeditationRetreatLevels } from "@/hooks/query-hooks/useLevels";
import { Button, Flex, Input, InputRef, TableColumnType, Tag } from "antd";
import { ColumnsType, TableProps } from "antd/es/table";
import { FilterDropdownProps } from "antd/es/table/interface";
import { useRef } from "react";
import { AiOutlineSearch } from "react-icons/ai";
import { getMeditatotStatusColor } from ".";
import { AntdTable } from "../common";
import { RegistrantGenderCountModal } from "./RegistrantGenderCountModal";

interface RegistrantTableProps<TActionProps = {meditationRetreat?: MeditationRetreat}> extends Omit<TableProps<MeditatorWithMeditationRetreat>, 'columns' | 'dataSource'> {
    dataSource: MeditatorWithMeditationRetreat[],
    renderColumnKeys: string[],
    TableActionComponent?: React.ComponentType<{ record: MeditatorWithMeditationRetreat } & TActionProps>,
    tableActionProps?: TActionProps,
    hasFilter?: {
        search?: boolean,
        status?: boolean,
        level?: boolean
    },
}

export function RegistrantTable<TActionProps = {meditationRetreat?: MeditationRetreat}>({
    dataSource,
    renderColumnKeys,
    TableActionComponent,
    tableActionProps,
    hasFilter = {
        search: false,
        status: false,
        level: false
    },
    ...props
}: RegistrantTableProps<TActionProps>) {

    const { data: levels } = useGetMeditationRetreatLevels();

    const searchInput = useRef<InputRef>(null);

    const handleReset = (clearFilters: () => void, confirm: FilterDropdownProps['confirm']) => {
        clearFilters();
        confirm();
    };

    const getColumnSearchProps = hasFilter?.search ? (dataIndex: keyof MeditatorWithMeditationRetreat["user"]): TableColumnType<MeditatorWithMeditationRetreat> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div
                className="p-2 space-y-2"
                onKeyDown={(e) => e.stopPropagation()}
            >
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => confirm()}
                    size="large"
                    allowClear
                    onClear={() => clearFilters && handleReset(clearFilters, confirm)}
                />
                <Flex
                    justify="flex-end"
                    gap={8}
                >
                    <Button
                        type="primary"
                        onClick={() => confirm()}
                        icon={<AiOutlineSearch />}
                        className="!font-poppins"
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters, confirm)}
                        className="!font-poppins"
                    >
                        Reset
                    </Button>
                </Flex>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <AiOutlineSearch
                size={16}
                style={{ color: filtered ? 'var(--color-f80)' : undefined }}
            />
        ),
        onFilter: (value, record) => {
            const fieldValue = record.user?.[dataIndex];
            return fieldValue
                ? fieldValue.toString().toLowerCase().includes((value as string).toLowerCase())
                : false;
        },
        filterDropdownProps: {
            onOpenChange(open: boolean) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
    }) : () => ({});

    const columns: ColumnsType<MeditatorWithMeditationRetreat> = [
        {
            title: TableTitles.No,
            dataIndex: "no",
            render: (value, record, index) => index + 1,
        },
        {
            title: TableTitles.registrant_id,
            dataIndex: ["user", "id"],
            key: "id"
        },
        {
            title: TableTitles.registrant_name,
            dataIndex: ["user", "name"],
            key: "name",
            ...getColumnSearchProps("name"),
            minWidth: 120
        },
        {
            title: TableTitles.retreat_level,
            dataIndex: ["user", "level", "name"],
            key: "level",
            filters: hasFilter?.level && levels ? levels?.map((level) => ({
                text: level?.name,
                value: level?.name
            })) : undefined,
            onFilter: (value, record) => record.user.level.name === value,
            minWidth: 160
        },
        {
            title: TableTitles.retreat_name,
            dataIndex: ["event", "title"],
            key: "title",
        },
        {
            title: TableTitles.retreat_start_date,
            dataIndex: ["event", "fromDate"],
            key: "fromDate",
        },
        {
            title: TableTitles.retreat_end_date,
            dataIndex: ["event", "toDate"],
            key: "toDate",
        },
        {
            title: TableTitles.total_registrants,
            dataIndex: ["event", "totalRegistrants"],
            key: "totalRegistrants",
            render: (value, record) => record?.events && <RegistrantGenderCountModal {...record.events?.[0]} />
        },
        {
            title: TableTitles.registrant_book_id,
            dataIndex: ["user", "book", "bookNo"],
            key: "bookNo",
            render: (value, record) => record.user.book ? record.user.book.bookNo : "N/A",
            minWidth: 120,
        },
        {
            title: TableTitles.registrant_phone,
            dataIndex: ["user", "phone"],
            key: "phone",
            render: (value) => value || "N/A",
            minWidth: 120
        },
        {
            title: TableTitles.registrant_nrc,
            dataIndex: ["user", "nrcNo"],
            key: "nrcNo",
            render: (value) => value || "N/A",
            minWidth: 200
        },
        {
            title: TableTitles.registrant_address,
            dataIndex: ["user", "address"],
            key: "address",
            render: (value) => value || "N/A",
            minWidth: 120
        },
        {
            title: TableTitles.registrant_status,
            dataIndex: ["user", "status"],
            key: "status",
            render: (value) => value === MeditatorStatus.ACCEPTED ? "-" : (
                <Tag
                    color={getMeditatotStatusColor(value)}
                >
                    {value}
                </Tag>
            ),
            filters: hasFilter?.status ? [MeditatorStatus.PASSED, MeditatorStatus.FAILED].map((status) => ({
                text: status,
                value: status
            })) : undefined,
            onFilter: (value, record) => record.user.status === value,
        },
        {
            title: TableTitles.action,
            dataIndex: "action",
            key: "action",
            render: (value, record) => TableActionComponent && <TableActionComponent record={record} {...(tableActionProps as TActionProps)} />,
            fixed: "right",
        }
    ]

    const renderColumns = columns.filter((col) => renderColumnKeys?.includes(col.title as string));

    return (
        <>
            <AntdTable
                columns={renderColumns}
                dataSource={dataSource}
                rowKey={r => r.user.id}
                {...props}
            />
        </>
    )
}
